import CloseIcon from "@mui/icons-material/Close";
import KeyboardIcon from "@mui/icons-material/KeyboardOutlined";
import { Box, IconButton, Popover, Typography } from "@mui/material";
import React from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { zinc } from "@/ui/tokens";
import { DRIVER_SHORTCUT_CATEGORY_ORDER, DRIVER_SHORTCUT_LIST, type DriverShortcut } from "./keyboard/driverShortcuts";

function Keycap({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="kbd"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 22,
        height: 22,
        px: 0.75,
        borderRadius: 1,
        bgcolor: zinc[700],
        border: `1px solid ${zinc[600]}`,
        boxShadow: "0 1px 0 rgba(0,0,0,0.4)",
        color: "white",
        fontSize: "0.75rem",
        fontWeight: 600,
        fontFamily: "inherit",
        lineHeight: 1,
      }}
    >
      {children}
    </Box>
  );
}

function ShortcutRow({ shortcut }: { shortcut: DriverShortcut }) {
  return (
    <Box
      data-sb={TestIds.driverView.keyboardShortcutRow}
      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, py: 0.5 }}
    >
      <Typography sx={{ color: zinc[300], fontSize: "0.8125rem" }}>{shortcut.label}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
        {shortcut.displayKeys.map((group, groupIndex) => (
          <React.Fragment key={group.join("+")}>
            {groupIndex > 0 && <Typography sx={{ color: zinc[500], fontSize: "0.75rem" }}>or</Typography>}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              {group.map((cap) => (
                <Keycap key={cap}>{cap}</Keycap>
              ))}
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Lists every driver-table shortcut, grouped by category, straight from the {@link DRIVER_SHORTCUT_LIST} registry.
 * Rendered as a popover anchored under the header keyboard icon so it drops from the top bar rather than floating.
 */
export default function KeyboardShortcutsPopover({
  open,
  anchorEl,
  onClose,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}) {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: {
            width: 360,
            maxWidth: "calc(100vw - 16px)",
            bgcolor: zinc[800],
            border: `1px solid ${zinc[600]}`,
            backgroundImage: "none",
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
            mt: 0.5,
          },
        },
      }}
    >
      <Box data-sb={TestIds.driverView.keyboardShortcuts} sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <KeyboardIcon sx={{ color: "white", fontSize: 20 }} />
          <Typography sx={{ color: "white", fontSize: "0.9375rem", fontWeight: 600, flexGrow: 1 }}>
            Keyboard shortcuts
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: zinc[400] }} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {DRIVER_SHORTCUT_CATEGORY_ORDER.map((category) => {
          const shortcuts = DRIVER_SHORTCUT_LIST.filter((s) => s.category === category);
          if (shortcuts.length === 0) return null;
          return (
            <Box key={category} sx={{ mb: 1.5, "&:last-of-type": { mb: 0 } }}>
              <Typography
                sx={{
                  color: zinc[500],
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  mb: 0.5,
                }}
              >
                {category}
              </Typography>
              {shortcuts.map((shortcut) => (
                <ShortcutRow key={shortcut.id} shortcut={shortcut} />
              ))}
            </Box>
          );
        })}
      </Box>
    </Popover>
  );
}
